import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    CircleCheck,
    Info,
    Pause,
    Play,
    RotateCcw,
    Wind
} from 'lucide-react'

import { cn } from '@/lib/utils'

// The circle's scale at the bottom and top of a breath. Inhales travel from
// EMPTY to FULL, exhales back again, holds stay where the last phase ended.
const EMPTY = 0.52
const FULL = 1

interface Phase {
    label: string
    seconds: number
    from: number
    to: number
}

interface Pattern {
    key: string
    name: string
    timing: string
    note: string
    phases: Phase[]
}

const inhale = (seconds: number, to = FULL, from = EMPTY): Phase => ({
    label: 'Breathe in',
    seconds,
    from,
    to
})

const exhale = (seconds: number): Phase => ({ label: 'Breathe out', seconds, from: FULL, to: EMPTY })

const hold = (seconds: number, at: number): Phase => ({ label: 'Hold', seconds, from: at, to: at })

// Timings are editorial choices, not protocols copied from a single trial. The
// tool page says so. Slow breathing and the longer exhale both land on six
// breaths a minute; cyclic sighing follows the two-inhale, long-exhale shape
// described by Balban et al. (2023), who let people set their own pace.
const PATTERNS: Pattern[] = [
    {
        key: 'slow',
        name: 'Slow breathing',
        timing: '5 in, 5 out',
        note: 'Six breaths a minute, a pace used in many slow breathing studies. The best place to start.',
        phases: [inhale(5), exhale(5)]
    },
    {
        key: 'longer-exhale',
        name: 'Longer exhale',
        timing: '4 in, 6 out',
        note: 'The out-breath is longer than the in-breath. Still six breaths a minute.',
        phases: [inhale(4), exhale(6)]
    },
    {
        key: 'cyclic-sighing',
        name: 'Cyclic sighing',
        timing: '3 in, 1 top-up, 6 out',
        note: 'Breathe in through the nose, sip in a little more, then let it all out slowly.',
        phases: [
            inhale(3, 0.86),
            { label: 'Top up', seconds: 1, from: 0.86, to: FULL },
            exhale(6)
        ]
    },
    {
        key: 'box',
        name: 'Box breathing',
        timing: '4 in, 4 hold, 4 out, 4 hold',
        note: 'Equal counts with a pause at the top and bottom. Easy to remember anywhere.',
        phases: [inhale(4), hold(4, FULL), exhale(4), hold(4, EMPTY)]
    },
    {
        key: '4-7-8',
        name: '4-7-8 breathing',
        timing: '4 in, 7 hold, 8 out',
        note: 'Long breath holds. If holding feels uncomfortable, switch to slow breathing.',
        phases: [inhale(4), hold(7, FULL), exhale(8)]
    }
]

const DURATIONS = [1, 3, 5, 10]

type Status = 'idle' | 'running' | 'paused' | 'done'

const easeInOut = (value: number): number => (1 - Math.cos(Math.PI * value)) / 2

const formatRemaining = (ms: number): string => {
    const total = Math.max(0, Math.ceil(ms / 1000))
    const minutes = Math.floor(total / 60)
    const seconds = total % 60

    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const cycleLength = (pattern: Pattern): number =>
    pattern.phases.reduce((sum, phase) => sum + phase.seconds, 0) * 1000

// Where in the pattern a given moment falls: which phase, how far through it,
// and how big the circle should be.
const locate = (pattern: Pattern, elapsed: number) => {
    const cycleMs = cycleLength(pattern)
    let position = elapsed % cycleMs

    for (const phase of pattern.phases) {
        const phaseMs = phase.seconds * 1000
        if (position < phaseMs) {
            const progress = position / phaseMs
            return {
                phase,
                progress,
                secondsLeft: Math.ceil((phaseMs - position) / 1000),
                scale: phase.from + (phase.to - phase.from) * easeInOut(progress)
            }
        }
        position -= phaseMs
    }

    const last = pattern.phases[pattern.phases.length - 1]
    return { phase: last, progress: 1, secondsLeft: 0, scale: last.to }
}

export const BreathingGuide: FC = (): ReactNode => {
    const [patternKey, setPatternKey] = useState(PATTERNS[0].key)
    const [minutes, setMinutes] = useState(5)
    const [status, setStatus] = useState<Status>('idle')
    const [elapsed, setElapsed] = useState(0)
    const [reduceMotion, setReduceMotion] = useState(false)

    // Mirrors `elapsed` so the animation loop can resume from where a pause
    // left off without re-subscribing on every frame.
    const elapsedRef = useRef(0)

    const pattern = PATTERNS.find((item) => item.key === patternKey) ?? PATTERNS[0]
    const cycleMs = cycleLength(pattern)
    // A session always finishes on a complete breath rather than mid-inhale.
    const sessionMs = Math.ceil((minutes * 60000) / cycleMs) * cycleMs

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)')
        setReduceMotion(query.matches)

        const onChange = (event: MediaQueryListEvent) => setReduceMotion(event.matches)
        query.addEventListener('change', onChange)
        return () => query.removeEventListener('change', onChange)
    }, [])

    useEffect(() => {
        if (status !== 'running') return

        let frame = 0
        const origin = performance.now() - elapsedRef.current

        const tick = (now: number) => {
            const next = now - origin

            if (next >= sessionMs) {
                elapsedRef.current = sessionMs
                setElapsed(sessionMs)
                setStatus('done')
                return
            }

            elapsedRef.current = next
            setElapsed(next)
            frame = requestAnimationFrame(tick)
        }

        frame = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(frame)
    }, [status, sessionMs])

    // Keep the screen awake during a session where the browser allows it, so a
    // phone does not dim halfway through a ten minute practice.
    useEffect(() => {
        if (status !== 'running' || !('wakeLock' in navigator)) return

        let sentinel: WakeLockSentinel | null = null
        let cancelled = false

        navigator.wakeLock
            .request('screen')
            .then((lock) => {
                if (cancelled) lock.release().catch(() => undefined)
                else sentinel = lock
            })
            .catch(() => undefined)

        return () => {
            cancelled = true
            sentinel?.release().catch(() => undefined)
        }
    }, [status])

    const reset = () => {
        elapsedRef.current = 0
        setElapsed(0)
        setStatus('idle')
    }

    const choosePattern = (key: string) => {
        setPatternKey(key)
        reset()
    }

    const chooseMinutes = (value: number) => {
        setMinutes(value)
        reset()
    }

    const toggle = () => {
        if (status === 'running') {
            setStatus('paused')
            return
        }

        if (status === 'done') {
            elapsedRef.current = 0
            setElapsed(0)
        }

        setStatus('running')
    }

    const active = status === 'running' || status === 'paused'
    const current = locate(pattern, elapsed)
    const circleScale = !active ? EMPTY : reduceMotion ? 0.8 : current.scale
    const breaths = Math.floor(elapsed / cycleMs)
    const perMinute = Math.round((60000 / cycleMs) * 10) / 10

    const centreLabel =
        status === 'done'
            ? 'Done'
            : status === 'idle'
                ? 'Ready'
                : status === 'paused'
                    ? 'Paused'
                    : current.phase.label

    return (
        <section
            aria-label='Guided breathing timer'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Wind className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Guided breathing timer
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Pick a pattern, press start, and breathe with the circle.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div role='group' aria-label='Breathing pattern' className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                    {PATTERNS.map((item) => (
                        <button
                            key={item.key}
                            type='button'
                            aria-pressed={item.key === pattern.key}
                            onClick={() => choosePattern(item.key)}
                            className={cn(
                                'flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors',
                                item.key === pattern.key
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border bg-background text-foreground hover:bg-secondary'
                            )}
                        >
                            <span className='text-sm font-semibold'>{item.name}</span>
                            <span
                                className={cn(
                                    'mt-0.5 text-xs',
                                    item.key === pattern.key
                                        ? 'text-primary-foreground/80'
                                        : 'text-muted-foreground'
                                )}
                            >
                                {item.timing}
                            </span>
                        </button>
                    ))}
                </div>

                <span className='mt-3 block text-sm leading-relaxed text-muted-foreground'>{pattern.note}</span>

                <div className='mt-5 flex flex-wrap items-center gap-3'>
                    <label htmlFor='breathing-guide-minutes' className='text-sm font-medium text-foreground'>
                        Session length
                    </label>
                    <select
                        id='breathing-guide-minutes'
                        value={minutes}
                        onChange={(event) => chooseMinutes(Number(event.target.value))}
                        className='rounded-xl border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    >
                        {DURATIONS.map((option) => (
                            <option key={option} value={option}>
                                {option === 1 ? '1 minute' : `${option} minutes`}
                            </option>
                        ))}
                    </select>
                    <span className='text-sm text-muted-foreground'>
                        About {perMinute} breaths a minute
                    </span>
                </div>

                <div className='mt-8 flex flex-col items-center'>
                    <div className='relative flex size-60 items-center justify-center sm:size-72'>
                        <span aria-hidden='true' className='absolute inset-0 rounded-full border-2 border-dashed border-primary/25' />
                        <span
                            aria-hidden='true'
                            className='absolute inset-0 rounded-full bg-primary/15'
                            style={{ transform: `scale(${circleScale})` }}
                        />
                        <span
                            aria-hidden='true'
                            className='absolute inset-[18%] rounded-full bg-primary/25'
                            style={{ transform: `scale(${circleScale})` }}
                        />

                        <span className='relative flex flex-col items-center text-center'>
                            <span
                                aria-live='polite'
                                className='font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'
                            >
                                {centreLabel}
                            </span>
                            {status === 'running' && (
                                <span aria-hidden='true' className='mt-1 text-4xl font-light tabular-nums text-foreground/80'>
                                    {current.secondsLeft}
                                </span>
                            )}
                        </span>
                    </div>

                    {active && reduceMotion && (
                        <div className='mt-4 h-1.5 w-48 overflow-hidden rounded-full bg-secondary'>
                            <div
                                className='h-full rounded-full bg-primary'
                                style={{ width: `${Math.round(current.progress * 100)}%` }}
                            />
                        </div>
                    )}

                    <div className='mt-6 flex items-center gap-3'>
                        <button
                            type='button'
                            onClick={toggle}
                            className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                        >
                            {status === 'running' ? <Pause className='size-4' /> : <Play className='size-4' />}
                            {status === 'running'
                                ? 'Pause'
                                : status === 'paused'
                                    ? 'Resume'
                                    : status === 'done'
                                        ? 'Go again'
                                        : 'Start'}
                        </button>

                        {status !== 'idle' && (
                            <button
                                type='button'
                                onClick={reset}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            >
                                <RotateCcw className='size-4' />
                                Reset
                            </button>
                        )}
                    </div>

                    <span className='mt-4 text-sm tabular-nums text-muted-foreground'>
                        {status === 'idle'
                            ? `${formatRemaining(sessionMs)} session`
                            : `${formatRemaining(sessionMs - elapsed)} left, ${breaths} ${breaths === 1 ? 'breath' : 'breaths'} done`}
                    </span>
                </div>

                {status === 'done' && (
                    <div className='mt-6 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                        <span className='flex items-center gap-2 text-sm font-semibold text-foreground'>
                            <CircleCheck className='size-4 shrink-0 text-primary' />
                            Session complete: {breaths} breaths in {formatRemaining(sessionMs)}
                        </span>
                        <span className='mt-3 block text-sm text-muted-foreground'>Where to next:</span>
                        <span className='mt-1 flex flex-col gap-1 text-sm'>
                            <Link href='/breathwork/slow-breathing-heart-rate' className='font-medium text-primary no-underline hover:underline'>
                                Does slow breathing really change your heart rate?
                            </Link>
                            <Link href='/stress-management/stress-response-explained' className='font-medium text-primary no-underline hover:underline'>
                                What happens in your body when you are stressed
                            </Link>
                            <Link href='/breathwork' className='font-medium text-primary no-underline hover:underline'>
                                The complete guide to breathwork
                            </Link>
                        </span>
                    </div>
                )}

                <div className='mt-6 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        Sit or lie down. If you feel dizzy, light-headed or tingly, stop and breathe
                        normally. Never practise in water or while driving. See{' '}
                        <Link href='/breathwork/is-breathwork-safe' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            is breathwork safe?
                        </Link>
                    </span>
                </div>
            </div>
        </section>
    )
}

export default BreathingGuide
